import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import logsService from '../services/logsService';
import kpisService from '../services/kpisService';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusBadge from '../components/StatusBadge';
import KPICalculator from '../components/KPICalculator';

export default function Dashboard() {
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    recentLogs: [],
    latestKpi: null,
  });
  const [adminStats, setAdminStats] = useState(null);

  const isAdmin = user?.role?.name === 'admin';
  const isSupervisor = user?.role?.name === 'supervisor';

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        if (isAdmin) {
          // Fetch admin dashboard data
          const response = await api.get('/admin/dashboard');
          setAdminStats(response.data.data);
        } else {
          // Fetch driver dashboard data
          const [logsRes, kpisRes] = await Promise.allSettled([
            logsService.getLogs({ limit: 5 }),
            kpisService.getMyKpis({ period_type: 'monthly', limit: 1 }),
          ]);
          setStats({
            recentLogs: logsRes.status === 'fulfilled' ? (logsRes.value.data || []) : [],
            latestKpi: kpisRes.status === 'fulfilled' ? (kpisRes.value.data?.[0] || null) : null,
          });
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isAdmin]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Admin Dashboard
  if (isAdmin && adminStats) {
    return (
      <div className="space-y-6">
        {/* Welcome */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {user?.first_name}!
          </h1>
          <p className="text-gray-600 mt-1">
            {format(new Date(), 'EEEE, MMMM d, yyyy')} - Admin Dashboard
          </p>
        </div>

        {/* Admin Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">{adminStats.users?.total || 0}</div>
                <div className="text-sm text-gray-600">Total Users</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">{adminStats.drivers?.total || 0}</div>
                <div className="text-sm text-gray-600">Total Drivers</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">{adminStats.vehicles?.active || 0}</div>
                <div className="text-sm text-gray-600">Active Vehicles</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">{adminStats.logs?.pendingApprovals || 0}</div>
                <div className="text-sm text-gray-600">Pending Approvals</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Link
            to="/admin/users"
            className="bg-primary-600 text-white rounded-lg p-6 hover:bg-primary-700 transition-colors"
          >
            <div className="flex items-center">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              <div className="ml-4">
                <div className="text-lg font-semibold">Manage Users</div>
                <div className="text-sm opacity-90">Add and manage users</div>
              </div>
            </div>
          </Link>

          <Link
            to="/admin/drivers"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="ml-4">
                <div className="text-lg font-semibold text-gray-900">Manage Drivers</div>
                <div className="text-sm text-gray-600">Assign supervisors</div>
              </div>
            </div>
          </Link>

          <Link
            to="/supervisor/approvals"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div className="ml-4">
                <div className="text-lg font-semibold text-gray-900">Approvals</div>
                <div className="text-sm text-gray-600">{adminStats.logs?.pendingApprovals || 0} pending</div>
              </div>
            </div>
          </Link>

          <Link
            to="/logs"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <div className="text-lg font-semibold text-gray-900">All Logs</div>
                <div className="text-sm text-gray-600">{adminStats.logs?.thisMonth || 0} this month</div>
              </div>
            </div>
          </Link>
        </div>

        {/* Logs Summary */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4">System Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-primary-600">{adminStats.logs?.today || 0}</div>
              <div className="text-sm text-gray-600">Logs Today</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-primary-600">{adminStats.logs?.thisMonth || 0}</div>
              <div className="text-sm text-gray-600">Logs This Month</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-primary-600">{adminStats.vehicles?.total || 0}</div>
              <div className="text-sm text-gray-600">Total Vehicles</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{adminStats.logs?.pendingApprovals || 0}</div>
              <div className="text-sm text-gray-600">Pending Approvals</div>
            </div>
          </div>
        </div>

        {/* KPI Calculator */}
        <KPICalculator />
      </div>
    );
  }

  const isDriver = !isAdmin && !isSupervisor;

  // Driver/Supervisor Dashboard
  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.first_name}!
        </h1>
        <p className="text-gray-600 mt-1">
          {format(new Date(), 'EEEE, MMMM d, yyyy')}
        </p>
      </div>

      {/* Supervisor Quick Actions */}
      {isSupervisor && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/supervisor/approvals"
            className="bg-primary-600 text-white rounded-lg p-6 hover:bg-primary-700 transition-colors"
          >
            <div className="flex items-center">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              <div className="ml-4">
                <div className="text-lg font-semibold">Pending Approvals</div>
                <div className="text-sm opacity-90">Review driver logs</div>
              </div>
            </div>
          </Link>

          <Link
            to="/logs"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <div className="text-lg font-semibold text-gray-900">Team Logs</div>
                <div className="text-sm text-gray-600">View your drivers' logs</div>
              </div>
            </div>
          </Link>
        </div>
      )}

      {/* Driver Quick Actions */}
      {isDriver && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/logs/new"
            className="bg-primary-600 text-white rounded-lg p-6 hover:bg-primary-700 transition-colors"
          >
            <div className="flex items-center">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <div className="ml-4">
                <div className="text-lg font-semibold">New Log Entry</div>
                <div className="text-sm opacity-90">Create today's log</div>
              </div>
            </div>
          </Link>

          <Link
            to="/logs"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <div className="text-lg font-semibold text-gray-900">View Logs</div>
                <div className="text-sm text-gray-600">{stats.recentLogs.length} recent entries</div>
              </div>
            </div>
          </Link>

          <Link
            to="/kpis"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-4">
                <div className="text-lg font-semibold text-gray-900">My KPIs</div>
                <div className="text-sm text-gray-600">
                  Score: {stats.latestKpi?.overall_score?.toFixed(1) || 'N/A'}
                </div>
              </div>
            </div>
          </Link>
        </div>
      )}

      {/* KPI Summary */}
      {stats.latestKpi && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4">Monthly Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600">{stats.latestKpi.total_trips || 0}</div>
              <div className="text-sm text-gray-600">Total Trips</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600">{parseFloat(stats.latestKpi.total_distance || 0).toFixed(0)}</div>
              <div className="text-sm text-gray-600">Total Distance (km)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600">{parseFloat(stats.latestKpi.total_hours || 0).toFixed(1)}</div>
              <div className="text-sm text-gray-600">Hours Worked</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600">{parseFloat(stats.latestKpi.fuel_efficiency || 0).toFixed(1)}</div>
              <div className="text-sm text-gray-600">Fuel Efficiency</div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Logs */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Recent Log Entries</h2>
          <Link to="/logs" className="text-primary-600 hover:underline text-sm">
            View All
          </Link>
        </div>

        {stats.recentLogs.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No log entries yet</p>
        ) : (
          <div className="space-y-3">
            {stats.recentLogs.map((log) => (
              <Link
                key={log.id}
                to={`/logs/${log.id}`}
                className="block p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">
                      {format(new Date(log.log_date), 'MMMM d, yyyy')}
                    </div>
                    <div className="text-sm text-gray-600">
                      {log.total_distance ? `${log.total_distance} km` : 'No distance recorded'}
                    </div>
                  </div>
                  <StatusBadge status={log.status} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
